import {
  addDaysToKey,
  formatCurrency,
  localDayKey,
  formatPlanStartTime,
  isDateOnlyPast,
  type BookingStageKind,
  type EventBooking,
} from '@peles/shared';
import { shortDay } from '../needs/needDisplay';

/** The `StatusBadge` colour of each kind of stage. */
export const BOOKING_KIND_COLORS: Record<BookingStageKind, string> = {
  open: 'yellow',
  booked: 'green',
  declined: 'gray',
};

const money = (n: number) => formatCurrency(n, { wholeDollars: true });

/** What a booking line is made of — the office's booking or a vendor's own. */
export interface BookingLineParts {
  categories: readonly string[];
  needCount: number;
  /** `HH:MM` */
  arrivalTime?: string | null;
}

/** A booking's money, in cents — the office's booking or a vendor's own. */
export interface BookingMoneyFigures {
  price: number;
  paid: number;
  balance: number;
  /** `YYYY-MM-DD` */
  nextDue: string | null;
}

/** "Flowers · 2 needs · arr. 3:00 PM" from its parts. */
export function describeBookingParts({
  categories,
  needCount,
  arrivalTime,
}: BookingLineParts): string {
  const arrival = formatPlanStartTime(arrivalTime);
  return [
    categories.slice(0, 2).join(', ') || null,
    needCount ? `${needCount} ${needCount === 1 ? 'need' : 'needs'}` : null,
    arrival ? `arr. ${arrival}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "Flowers · 2 needs · arr. 3:00 PM" — what a booking is, in one line. */
export function describeBooking(b: EventBooking): string {
  return describeBookingParts({
    categories: b.categories,
    needCount: b.needs.length,
    arrivalTime: b.metadata.arrivalTime,
  });
}

/** "$4,950 · next due Oct 1", "$650 · paid in full", or "" with nothing booked. */
export function describeMoney(m: BookingMoneyFigures, hasNeeds: boolean): string {
  if (!hasNeeds) return '';
  const tail =
    m.balance <= 0.005
      ? 'paid in full'
      : m.nextDue
        ? `next due ${shortDay(m.nextDue)}`
        : `${money(m.balance)} left`;
  return `${money(m.price)} · ${tail}`;
}

/** "$4,950 · next due Oct 1", "$650 · paid in full", or "" with nothing booked. */
export function describeBookingMoney(b: EventBooking): string {
  return describeMoney(b, b.needs.length > 0);
}

/** Share of the price paid, 0–100, whole. */
export function moneyPaidPercent(m: BookingMoneyFigures, hasNeeds: boolean): number {
  if (m.price <= 0) return hasNeeds ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round((m.paid / m.price) * 100)));
}

/** Share of the booked price paid, 0–100, whole. */
export function paidPercent(b: EventBooking): number {
  return moneyPaidPercent(b, b.needs.length > 0);
}

/** A date the booking depends on that has passed or is near: the insurance certificate. */
export function coiWarning(b: EventBooking): string | null {
  const day = b.metadata.coiExpiresAt;
  if (!day) return null;
  return isDateOnlyPast(day) ? `Insurance expired ${shortDay(day)}` : null;
}

/** How many days ahead a contract running out is flagged on the vendor's row. */
export const CONTRACT_WARNING_DAYS = 30;

/**
 * The contract's end, when it has passed (red) or is within
 * `CONTRACT_WARNING_DAYS` (amber) — a contract that lapses before the event is
 * one to renew, like the insurance certificate.
 */
export function contractWarning(
  b: EventBooking,
  today: string = localDayKey(),
): { label: string; color: 'red' | 'yellow' } | null {
  const day = b.metadata.contractExpiresAt;
  if (!day) return null;
  if (day < today) return { label: `Contract expired ${shortDay(day)}`, color: 'red' };
  if (day <= addDaysToKey(today, CONTRACT_WARNING_DAYS)) {
    return { label: `Contract expires ${shortDay(day)}`, color: 'yellow' };
  }
  return null;
}

/** `tel:` / `sms:` links — the shared helper, kept importable from here. */
export { phoneHref } from '@peles/shared';
